import { appConfirm } from './confirm-dialog.js';
import { navigate, shownPath } from './calendar-spa.js';
import { phoneQuery } from './phone.js';

/**
 * 업무 요청 상세 패널 (§11, 2026-09-20).
 *
 *  · /work/requests 의 행·카드는 그대로 <a href="/work/requests/:id"> 이고 `data-work-open`
 *    을 실어 둔다 — JS 가 없으면 상세 화면으로 그냥 간다(딥링크·no-JS 경로 불변).
 *  · 클릭을 문서 **캡처** 단계에서 가로채 `GET /work/requests/:id?partial=1` 조각(서버의
 *    renderWorkFragment)을 #workPanelBody 에 넣는다. 캡처인 이유: calendar-spa 의 문서
 *    클릭 리스너가 먼저 등록돼 있으면 같은 클릭을 SPA 이동으로 삼켜 버린다.
 *  · URL 은 ?req=<id> 로 동기한다 — 처음 열 때만 push(뒤로가기 = 닫힘), 그 밖엔 replace
 *    (crm.js syncDealURL · hr-panel.js syncURL 과 같은 계약).
 *  · 패널 안 행동(시작·검토 요청·승인·반려·댓글·체크)은 지금의 JSON POST 그대로 보내고,
 *    성공하면 ① 조각을 다시 받고 ② navigate(location.href, {swap:'#workList'}) 로 목록
 *    행만 갱신한다. 409 는 덮어쓰지 않고 조각만 다시 받는다.
 *  · Esc·바깥 클릭·✕ 로 닫고, 닫으면 포커스는 열었던 행으로 돌아간다. ≤768px 은 바텀 시트.
 *  · 미저장 입력이 있으면 닫기·다른 행만이 아니라 링크(탭·정렬·사이드바)와 브라우저의 뒤로·앞으로
 *    가기도 먼저 묻는다(백로그 BF3) — 같은 경로의 뒤로 가기(?req= 가 빠지는 칸)는 이 모듈의 캡처
 *    popstate 가 칸을 되감은 뒤 묻고, 다른 경로는 라우터의 calendar:before-navigate(detail.popstate)
 *    를 막아 라우터가 되감게 한다. 확인하면 같은 이동을 다시 한다.
 */

const panelEl = () => document.getElementById('workPanel');
const bodyEl = () => document.getElementById('workPanelBody');
const listEl = () => document.getElementById('workList');
const labels = () => { try { return JSON.parse(document.getElementById('workLabels')?.textContent || '{}'); } catch (_) { return {}; } };
const CSRF = () => document.querySelector('meta[name=csrf-token]')?.content || '';
const isOpen = () => { const p = panelEl(); return !!p && !p.hidden; };
// 패널 밖에서 열린 오버레이(확인 대화·사람 피커·컨텍스트 메뉴)의 클릭·Esc 는 패널 것이 아니다.
const overlayOpen = () => !!document.querySelector('.confirm-dialog-overlay.show, .pp-pop, .pp-sheet-root, .ctx-menu');

const state = { id: '', opener: null, seq: 0, busy: false, baseline: '', href: '', idx: null, path: '', reverting: false };

// 기록에서 지금 칸의 자리 (Navigation API 가 있는 브라우저만 안다 — calendar-spa 의 entryIndex 와 같다).
function entryIndex() {
    try {
        const e = window.navigation && window.navigation.currentEntry;
        return e && typeof e.index === 'number' && e.index >= 0 ? e.index : null;
    } catch (_) { return null; }
}
// 패널이 열려 있는 칸 — 막힌 뒤로·앞으로 가기가 되돌아올 자리다.
function rememberEntry() {
    state.href = window.location.href;
    state.idx = entryIndex();
    state.path = window.location.pathname;
}

// 패널 안 폼의 입력을 잃지 않도록: 조각을 받은 직후의 값을 기준선으로 잡고, 사용자가 닫으려
// 할 때(✕ · Esc · 바깥 클릭 · 다른 행 · 패널 밖 링크 · 뒤로·앞으로 가기) work.js 와 같은 appConfirm 을
// 띄운다(뒤로·앞으로·링크는 백로그 BF3 — guardPop · onBeforeNavigate · onClick). 목록 필터의 입력(검색
// 칸 · select)은 아직 묻지 않고 닫는다 — 입력 중에 대화상자를 띄울 자리가 없다.
const panelForms = () => [...(bodyEl()?.querySelectorAll('[data-work-form]') || [])];
const serialized = () => JSON.stringify(panelForms().map((f) => [...new FormData(f)].filter(([name]) => !['if_version', '_csrf'].includes(name))));
const dirty = () => isOpen() && !!state.baseline && state.baseline !== serialized();
const confirmDiscard = () => (dirty() ? appConfirm({ message: labels().unsaved || '' }) : Promise.resolve(true));

// 데스크톱 도크는 목록을 덮지 않고 가림막도 없으며 포커스도 가두지 않는다 — 그 상태의
// aria-modal 은 보조기기에 "나머지 화면은 없다" 고 거짓말한다. 바텀 시트일 때만 켠다.
const sheetModal = phoneQuery;
function syncModal() {
    const sheet = panelEl()?.querySelector('.work-panel-sheet');
    if (!sheet) return;
    if (sheetModal.matches) sheet.setAttribute('aria-modal', 'true');
    else sheet.removeAttribute('aria-modal');
}

function note(text, error) {
    const p = document.createElement('p');
    p.className = 'work-panel-note' + (error ? ' error' : '');
    p.textContent = text || '';
    return p;
}

function feedback(message, error) {
    const el = document.getElementById('workPanelFeedback');
    if (!el) return;
    el.hidden = !message;
    el.classList.toggle('error', !!error);
    el.textContent = message || '';
    if (message) el.focus({ preventScroll: true });
}

// 열린 요청의 행을 표시한다 — 목록을 갈아끼운 뒤에도 다시 부른다(노드가 새것이다).
function markRow() {
    document.querySelectorAll('[data-work-open]').forEach((el) => {
        const on = !!state.id && el.dataset.workOpen === state.id;
        el.classList.toggle('work-open-active', on);
        const row = el.closest('tr');
        if (row) row.classList.toggle('work-row-active', on);
    });
}

function syncURL(id) {
    const u = new URL(window.location.href);
    const had = u.searchParams.get('req') || '';
    if (had === (id || '')) return;
    const fresh = !had;
    if (id) u.searchParams.set('req', id); else u.searchParams.delete('req');
    const st = Object.assign({}, window.history.state, { spa: true });
    const href = u.pathname + (u.search || '') + u.hash;
    if (id && fresh) window.history.pushState(st, '', href); else window.history.replaceState(st, '', href);
}

async function load(id) {
    const p = panelEl(), body = bodyEl();
    if (!p || !body || !id) return false;
    const seq = ++state.seq;
    body.replaceChildren(note(p.dataset.loading || ''));
    let res = null;
    try {
        res = await fetch('/work/requests/' + encodeURIComponent(id) + '?partial=1', { credentials: 'same-origin', headers: { Accept: 'text/html' } });
    } catch (_) { res = null; }
    if (seq !== state.seq) return false; // 그 사이 다른 요청을 열었거나 패널을 닫았다
    // 세션이 끊기면(또는 비밀번호 변경·TOTP 등록이 걸리면) 이 요청은 401 JSON 이 아니라
    // 303 → /login 이고 fetch 가 그것을 **따라가** 200 으로 돌아온다 — /work 경로는 /api/ 도
    // JSON Accept 도 아니라 wantsJSONAuthError 가 아니다. 그대로 넣으면 로그인 문서가 통째로
    // 패널에 들어와 <html> 이 겹치고 csrf-meta·#workFeedback 이 둘이 된다. redirected 와 조각
    // 표식으로 막고 「전체 화면」으로 보내면 진짜 이동이라 /login 에 제대로 내린다.
    if (!res || !res.ok || res.redirected || res.headers.get('X-Work-Fragment') !== '1') {
        state.baseline = '';
        const link = document.createElement('a');
        link.className = 'btn btn-secondary';
        link.href = '/work/requests/' + id;
        link.dataset.noSpa = '';
        link.textContent = p.dataset.full || '';
        body.replaceChildren(note(p.dataset.failed || labels().failed || '', true), link);
        return false;
    }
    body.innerHTML = await res.text();
    state.baseline = serialized();
    return true;
}

function refreshList() {
    if (!listEl()) return Promise.resolve(false);
    return navigate(window.location.href, { swap: '#workList' }).catch(() => false).then((done) => { markRow(); return done; });
}

export function openWorkPanel(id, opts) {
    opts = opts || {};
    const p = panelEl();
    if (!p || !id) return;
    if (!opts.keepFocus) state.opener = opts.opener || document.activeElement;
    state.id = id;
    p.hidden = false;
    syncModal();
    document.documentElement.classList.add('work-panel-open');
    feedback('');
    markRow();
    syncURL(id);
    rememberEntry();
    load(id).then(() => {
        const sheet = p.querySelector('.work-panel-sheet');
        if (sheet) sheet.scrollTop = 0;
        if (!opts.keepFocus) document.getElementById('workPanelClose')?.focus({ preventScroll: true });
    });
}

export function closeWorkPanel(opts) {
    opts = opts || {};
    const p = panelEl();
    if (!p || p.hidden) return;
    state.seq++; // 날아오는 중인 조각은 버린다
    p.hidden = true;
    state.id = '';
    state.baseline = '';
    document.documentElement.classList.remove('work-panel-open');
    bodyEl()?.replaceChildren();
    feedback('');
    markRow();
    if (!opts.keepURL) syncURL('');
    const back = state.opener;
    state.opener = null;
    // 바깥을 눌러 닫을 때는 포커스를 빼앗지 않는다 — 방금 누른 것이 받아야 한다.
    if (opts.restoreFocus && back && document.contains(back) && back.focus) back.focus({ preventScroll: true });
}

async function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-work-form]') || !form.closest('#workPanelBody')) return;
    event.preventDefault();
    event.stopPropagation(); // work.js 의 문서 리스너가 같은 제출을 한 번 더 처리하지 않도록
    if (state.busy) return;
    state.busy = true;
    const p = panelEl(), L = labels(), id = state.id;
    const body = new FormData(form);
    if (event.submitter && event.submitter.name) body.set(event.submitter.name, event.submitter.value);
    const buttons = [...p.querySelectorAll('button')];
    buttons.forEach((b) => { b.disabled = true; });
    p.setAttribute('aria-busy', 'true');
    try {
        const response = await fetch(form.getAttribute('action'), {
            method: 'POST', body, credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-CSRF-Token': CSRF() },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const fallback = response.status === 409 ? L.conflict : response.status === 403 ? L.forbidden : L.failed;
            feedback(typeof data.message === 'string' && data.message ? data.message : fallback, true);
            // 409 는 덮어쓰지 않고 최신 상태를 보인다 — 남이 바꾼 값이니 목록 행도 함께 맞춘다.
            if (response.status === 409) { await load(id); await refreshList(); }
            return;
        }
        await load(id);
        feedback(data.message || L.saved, false);
        await refreshList();
    } catch (_) {
        feedback(L.failed, true);
    } finally {
        state.busy = false;
        panelEl()?.removeAttribute('aria-busy');
        buttons.forEach((b) => { if (b.isConnected) b.disabled = false; });
    }
}

function onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!panelEl()) return;
    // 미저장 입력을 두고 패널 밖의 링크(탭·정렬·페이지·사이드바)를 누르면 이동하기 **전에** 묻는다 (BF3) —
    // 같은 경로의 이동은 라우터의 before-navigate 를 거치지 않고 끝나자마자 패널을 닫는다. 확인하면 기준선을
    // 비우고 같은 링크를 다시 누른다(그때는 dirty 가 아니므로 평소 길로 간다).
    const link = event.target.closest('a[href]');
    if (link && dirty() && !link.closest('#workPanel') && !link.matches('[data-work-open]') && !link.target && !link.hasAttribute('download')
        && !(link.getAttribute('href') || '').startsWith('#') && !overlayOpen()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        appConfirm({ message: labels().unsaved || '' }).then((ok) => {
            if (!ok) return;
            state.baseline = '';
            if (link.isConnected) link.click(); else window.location.assign(link.href);
        });
        return;
    }
    const open = event.target.closest('[data-work-open]');
    if (open) {
        event.preventDefault();
        const id = open.dataset.workOpen;
        confirmDiscard().then((ok) => { if (ok) openWorkPanel(id, { opener: open }); });
        return;
    }
    if (!isOpen()) return;
    if (event.target.closest('[data-work-panel-close]')) {
        event.preventDefault();
        confirmDiscard().then((ok) => { if (ok) closeWorkPanel({ restoreFocus: true }); });
        return;
    }
    if (event.target.closest('#workPanel') || overlayOpen()) return;
    confirmDiscard().then((ok) => { if (ok) closeWorkPanel(); });
}

// 브라우저의 뒤로·앞으로 가기 (BF3). 캡처라 라우터(calendar-spa)의 popstate 보다 먼저 돈다 — 창에 걸린
// 캡처 리스너는 같은 대상의 버블 리스너보다 먼저 불린다. 같은 경로에서 ?req= 가 바뀌는(패널이 닫히거나
// 다른 요청으로 바뀌는) 칸으로 가려는데 패널에 미저장 입력이 있으면: 라우터·아래 리스너를 멈추고, 패널의
// 칸으로 되감은 뒤 묻고, 확인하면 같은 만큼 다시 간다. 되감은 칸의 popstate 는 조용히 삼킨다.
function guardPop(event) {
    if (state.reverting) {
        state.reverting = false;
        if (entryIndex() === state.idx) { event.stopImmediatePropagation(); return; }
    }
    if (!isOpen() || !dirty()) return;
    const u = new URL(window.location.href);
    // 다른 경로로의 뒤로 가기는 라우터가 calendar:before-navigate(popstate) 를 보내고 onBeforeNavigate 가 막는다.
    if (u.pathname !== state.path) return;
    if ((u.searchParams.get('req') || '') === state.id) return;
    event.stopImmediatePropagation();
    const at = entryIndex();
    let redo;
    if (at != null && state.idx != null && at !== state.idx) {
        const delta = at - state.idx;
        state.reverting = true;
        window.history.go(-delta);
        redo = () => window.history.go(delta);
    } else {
        // 칸의 자리를 모르는 브라우저: 패널의 주소를 새 칸으로 밀어 넣는다(앞 칸이 잘린다 — 라우터와 같은 폴백).
        window.history.pushState(Object.assign({}, window.history.state, { spa: true }), '', state.href);
        redo = () => window.history.back();
    }
    appConfirm({ message: labels().unsaved || '' }).then((ok) => {
        if (!ok) return;
        state.baseline = '';
        redo();
    });
}

// 다른 경로로의 이동(링크의 SPA 이동, 뒤로·앞으로 가기)은 라우터가 먼저 묻는다 — 막으면 라우터가 화면과
// 기록을 그대로 두고(popstate 면 칸을 되감고), 확인하면 평가 화면처럼 navigate(href, {force:true}) 로 다시 간다.
function onBeforeNavigate(event) {
    if (!dirty()) return;
    event.preventDefault();
    const href = event.detail && event.detail.href;
    appConfirm({ message: labels().unsaved || '' }).then((ok) => {
        if (!ok) return;
        state.baseline = '';
        if (href) navigate(href, { force: true });
    });
}

function initPage() {
    const p = panelEl();
    if (!p) {
        state.id = ''; state.opener = null;
        document.documentElement.classList.remove('work-panel-open');
        return;
    }
    const want = new URLSearchParams(window.location.search).get('req') || '';
    if (want && (p.hidden || want !== state.id)) { openWorkPanel(want, { keepFocus: true }); return; }
    if (!want && !p.hidden) { closeWorkPanel({ keepURL: true }); return; }
    if (want && want === state.id) rememberEntry();
    markRow();
}

export function setupWorkPanel() {
    initPage();
    if (document.__workPanelBound) return;
    document.__workPanelBound = true;
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !isOpen() || overlayOpen()) return;
        event.preventDefault();
        confirmDiscard().then((ok) => { if (ok) closeWorkPanel({ restoreFocus: true }); });
    });
    window.addEventListener('popstate', guardPop, true);
    document.addEventListener('calendar:before-navigate', onBeforeNavigate);
    window.addEventListener('popstate', () => {
        if (!panelEl()) return;
        // 라우터가 다른 경로로의 뒤로 가기를 막았다면(되감는 중) 주소만 잠시 다른 경로다 — 패널은 그대로 둔다.
        if (window.location.pathname !== shownPath()) return;
        const want = new URLSearchParams(window.location.search).get('req') || '';
        if (want) { if (want !== state.id) openWorkPanel(want, { keepFocus: true }); return; }
        if (isOpen()) closeWorkPanel({ keepURL: true });
    });
    // 경로가 바뀌는 SPA 교체(다른 화면 → 목록)는 패널 노드를 새것으로 갈아끼운다.
    document.addEventListener('calendar:page-swapped', initPage);
    // 같은 경로의 제자리 이동(탭 · 기간 · 정렬 · 필터)은 #workList 만 갈아끼우므로
    // calendar:page-swapped 가 오지 않는다. 그 이동들은 주소를 req 없이 다시 쓰므로
    // (showWorkRequests 가 링크에서 지운다) markRow 만 걸면 주소와 패널이 어긋난다 —
    // initPage 를 걸어 ?req= 가 사라진 이동은 패널을 닫고, 그대로인 이동(액션 뒤
    // refreshList)은 want === state.id 라 markRow 로 떨어진다.
    document.addEventListener('calendar:navigated', initPage);
    // 바텀 시트 ↔ 데스크톱 도크가 바뀌면 aria-modal 도 따라간다.
    sheetModal.addEventListener('change', () => { if (isOpen()) syncModal(); });
}
